import { createRouter, createWebHistory, type RouteLocation } from 'vue-router';
import BaseLayout from '@/components/layouts/BaseLayout.vue';
import { ROUTE_NAMES } from '@/router/conf';
import { useCookies } from '@vueuse/integrations/useCookies';
import { EDisabledFeatures } from '@/types/composables/useDisabledFeatureEnums';
import { isMcpServerEnabled } from '@/composables/useFeatureFlag';
import { EPrintViewType } from '@/types/views/print/printView.enums';
import { updateTitle, waitForIt } from '@/helpers/utils';
import * as Sentry from '@sentry/vue';
import axiosInstance from '@/plugins/axios';
import { useQueryClient } from '@tanstack/vue-query';
import useAuthService from '@/composables/apiServices/useAuthService';
import useSnackbar from '@/composables/useSnackbar';
import i18n from '@/plugins/vueI18n';
// TODO: Update vite conf to load some settings views in one chunk https://router.vuejs.org/guide/advanced/lazy-loading.html#With-Vite
const AuthLayout = () => import('@/components/layouts/AuthLayout.vue');
const Auth2FALayout = () => import('@/components/layouts/Auth2FALayout.vue');
const PasswordView = () => import('@/views/auth/PasswordView.vue');
const WebAuthNView = () => import('@/views/auth/WebAuthNView.vue');
const TotpView = () => import('@/views/auth/TotpView.vue');
const UserSelectView = () => import('@/views/auth/UserSelectView.vue');
const ForcePasswordChangeView = () => import('@/views/auth/ForcePasswordChangeView.vue');
const ComposeView = () => import('@/views/ComposeView.vue');
const TabBaseLayout = () => import('@/components/layouts/TabBaseLayout.vue');
const AccountView = () => import('@/views/settings/AccountView.vue');
const SettingsLayout = () => import('@/components/layouts/SettingsLayout.vue');
const GeneralView = () => import('@/views/settings/GeneralView.vue');
const AppearanceView = () => import('@/views/settings/AppearanceView.vue');
const AutoReplyView = () => import('@/views/settings/AutoReplyView.vue');
const TwoFactor = () => import('@/views/settings/security/TwoFactorView.vue');
const AccountPassword = () => import('@/views/settings/security/AccountPasswordView.vue');
const AppPasswordView = () => import('@/views/settings/security/AppPasswordView.vue');
const EventsView = () => import('@/views/settings/security/EventsView.vue');
const ContentView = () => import('@/views/settings/security/ContentView.vue');
const SecurityView = () => import('@/views/settings/SecurityView.vue');
const MailboxLayout = () => import('@/views/mailbox/MailboxLayout.vue');
const SignaturesView = () => import('@/views/settings/signatures/SignaturesView.vue');
const SignaturesEditView = () => import('@/views/settings/signatures/SignaturesEditView.vue');
const ContactsView = () => import('@/views/settings/ContactsView.vue');
// const AddressbooksView = () => import('@/views/settings/contacts/AddressbooksView.vue');
const MyContactsView = () => import('@/views/settings/contacts/MyContactsView.vue');
const GroupsView = () => import('@/views/settings/contacts/GroupsView.vue');
const UpgradeView = () => import('@/views/upgrade/UpgradeView.vue');
const FoldersView = () => import('@/views/settings/FoldersView.vue');
const FiltersView = () => import('@/views/settings/FiltersView.vue');
const SourceView = () => import('@/views/message/SourceView.vue');
const PrintView = () => import('@/views/message/PrintView.vue');

const redirectToAuthApp = (to: RouteLocation): true | void => {
  if (import.meta.env.VITE_AUTH_BASE_URL) {
    console.debug('Redirecting to auth application');

    const { get } = useCookies([]);

    const lang =
      get(`zwma-lang`) ??
      (navigator.language.slice(0, 2) || import.meta.env.VITE_I18N_FALLBACK_LOCALE);

    const slash = import.meta.env.VITE_AUTH_BASE_URL.endsWith('/') ? '' : '/';

    const url = new URL(`${import.meta.env.VITE_AUTH_BASE_URL}${slash}${lang}`);

    if (to.query.reason) url.searchParams.set('reason', to.query.reason.toString());

    window.location.assign(url);

    // Return true to signify route navigation
    return true;
  }
  return;
};

const updatePageTitle = () => {
  updateTitle();
};

async function getFirstAvailableAccountIndex() {
  const queryClient = useQueryClient();
  const { getAccountsQuery } = useAuthService();
  const { get } = useCookies([]);

  const accounts = await queryClient.fetchQuery(getAccountsQuery());

  return accounts.find((account) => {
    const cookieDataForThisUser = get<string | undefined>(
      `token_expiry_u_${account.uid.toString()}`
    );
    return cookieDataForThisUser !== undefined;
  })?.uid;
}

const resolvedT = async (key: string) => {
  const { t } = i18n.global;
  await waitForIt(async () => t(key) !== key);
  return t(key);
};

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: ROUTE_NAMES.AUTH_LAYOUT,
      component: AuthLayout,
      beforeEnter: [updatePageTitle],
      children: [
        {
          path: '2fa',
          name: ROUTE_NAMES.AUTH_2FA,
          component: Auth2FALayout,
          beforeEnter: (to) => {
            // When going here directly always return to "login"
            if (to.name === ROUTE_NAMES.AUTH_2FA) {
              return { name: ROUTE_NAMES.AUTH_LOGIN_PASSWORD };
            }
          },
          children: [
            {
              path: 'totp',
              name: ROUTE_NAMES.AUTH_TOTP,
              component: TotpView,
              props: true
            },
            {
              path: 'webauthn',
              name: ROUTE_NAMES.AUTH_WEBAUTHN,
              component: WebAuthNView,
              props: true
            }
          ]
        },
        {
          path: 'password',
          name: ROUTE_NAMES.AUTH_LOGIN_PASSWORD,
          component: PasswordView
        },
        {
          path: 'force-password-change',
          name: ROUTE_NAMES.AUTH_FORCE_PASSWORD_CHANGE,
          component: ForcePasswordChangeView
        },
        {
          path: '',
          name: ROUTE_NAMES.AUTH_LOGIN_USER_SELECT,
          component: UserSelectView
        }
      ]
    },

    {
      path: '/u/:userIndex/tab/',
      component: TabBaseLayout,
      name: ROUTE_NAMES.BASE_LAYOUT_TAB,
      meta: {
        protected: true
      },
      beforeEnter: (to) => {
        // When going here directly always redirect to inbox
        if (to.name === ROUTE_NAMES.BASE_LAYOUT_TAB) {
          return { name: ROUTE_NAMES.BASE_LAYOUT, params: to.params };
        }
      },
      children: [
        {
          path: 'compose/:draftId?',
          name: ROUTE_NAMES.COMPOSE_TAB,
          component: ComposeView,
          meta: {
            protected: true
          },
          props: {
            withHeader: true
          }
        },
        {
          path: ':mailboxId/:threadId/:messageId',
          name: ROUTE_NAMES.MAILBOX_THREAD_TAB,
          component: MailboxLayout,
          meta: {
            protected: true
          }
        },
        {
          path: ':mailboxId/messages/:id/source',
          name: ROUTE_NAMES.MAILBOX_SOURCE,
          component: SourceView,
          meta: {
            protected: true
          }
        },
        {
          path: ':mailboxId/messages/:id/print',
          name: ROUTE_NAMES.MAILBOX_PRINT,
          component: PrintView,
          meta: {
            protected: true
          },
          props: (route) => ({
            messageId: route.params.id,
            mailboxId: route.params.mailboxId,
            printType: EPrintViewType.SINGLE
          })
        },
        {
          path: ':mailboxId/:threadId/print',
          name: ROUTE_NAMES.MAILBOX_THREAD_PRINT,
          component: PrintView,
          meta: {
            protected: true
          },
          props: (route) => ({
            threadId: route.params.threadId,
            mailboxId: route.params.mailboxId,
            printType: EPrintViewType.THREAD
          })
        }
      ]
    },
    {
      path: '/u/:userIndex/',
      component: BaseLayout,
      name: ROUTE_NAMES.BASE_LAYOUT,
      meta: {
        protected: true
      },
      children: [
        {
          path: 'compose/:draftId?',
          name: ROUTE_NAMES.COMPOSE,
          component: ComposeView,
          meta: {
            protected: true
          }
        },
        {
          path: 'settings',
          name: ROUTE_NAMES.SETTINGS_LAYOUT,
          component: SettingsLayout,
          meta: {
            label: 'settings.label',
            protected: true
          },
          children: [
            {
              path: 'appearance',
              name: ROUTE_NAMES.SETTINGS_APPEARANCE,
              component: AppearanceView,
              meta: {
                label: 'settings.appearance.label',
                protected: true
              }
            },
            {
              path: 'account',
              name: ROUTE_NAMES.SETTINGS_ACCOUNT,
              component: AccountView,
              meta: {
                label: 'settings.account.label',
                protected: true
              }
            },
            {
              path: 'signatures/edit/:signatureId?',
              name: ROUTE_NAMES.SETTINGS_SIGNATURE_EDIT,
              component: SignaturesEditView,
              meta: {
                label: 'settings.signatures.edit',
                protected: true,
                feature: EDisabledFeatures.SIGNATURES
              }
            },
            {
              path: 'signatures',
              name: ROUTE_NAMES.SETTINGS_SIGNATURE,
              component: SignaturesView,
              meta: {
                label: 'settings.signatures.label',
                protected: true,
                feature: EDisabledFeatures.SIGNATURES,
                featureInfo: 'settings.upgrade.viewList.signatures'
              }
            },
            {
              path: 'general',
              name: ROUTE_NAMES.SETTINGS_GENERAL,
              component: GeneralView,
              meta: {
                label: 'settings.general.label',
                protected: true
              }
            },
            {
              path: 'autoreply',
              name: ROUTE_NAMES.SETTINGS_AUTOREPLY,
              component: AutoReplyView,
              meta: {
                label: 'settings.autoreply.label',
                protected: true,
                feature: EDisabledFeatures.AUTOREPLY,
                featureInfo: 'settings.autoreply.label'
              }
            },
            {
              path: 'upgrade',
              name: ROUTE_NAMES.SETTINGS_UPGRADE,
              component: UpgradeView,
              meta: {
                label: 'settings.upgrade.label',
                protected: true
              }
            },
            {
              path: 'security',
              name: ROUTE_NAMES.SETTINGS_SECURITY,
              component: SecurityView,
              meta: {
                label: 'settings.security.label',
                protected: true
              },
              children: [
                {
                  name: ROUTE_NAMES.SECURITY_2FA,
                  path: '2fa',
                  component: TwoFactor,
                  meta: {
                    label: 'settings.security.2fa.label',
                    security: true,
                    protected: true
                  }
                },
                {
                  name: ROUTE_NAMES.SECURITY_APPLICATION_PASSWORDS,
                  path: 'asps',
                  component: AppPasswordView,
                  meta: {
                    label: 'settings.security.appPassword.label',
                    security: true,
                    protected: true
                  }
                },
                {
                  name: ROUTE_NAMES.SECURITY_ACCOUNT_PASSWORD,
                  path: 'password',
                  component: AccountPassword,
                  meta: {
                    label: 'settings.security.password.label',
                    security: true,
                    protected: true
                  }
                },
                {
                  name: ROUTE_NAMES.SECURITY_EVENTS,
                  path: 'events',
                  component: EventsView,
                  meta: {
                    label: 'settings.security.events.label',
                    security: true,
                    protected: true
                  }
                },
                {
                  name: ROUTE_NAMES.SECURITY_EXTERNAL_CONTENT,
                  path: 'content',
                  component: ContentView,
                  meta: {
                    label: 'settings.security.content.label',
                    security: true,
                    protected: true
                  }
                }
              ]
            },
            {
              path: 'contacts',
              name: ROUTE_NAMES.SETTINGS_CONTACTS,
              component: ContactsView,
              meta: {
                protected: true,
                label: 'settings.contacts.label',
                feature: EDisabledFeatures.CONTACTS,
                featureInfo: 'settings.upgrade.viewList.contacts'
              },
              children: [
                // {
                //   name: ROUTE_NAMES.CONTACTS_ADDRESSBOOKS,
                //   path: 'addressbooks',
                //   component: AddressbooksView,
                //   meta: {
                //     protected: true,
                //     contacts: true,
                //     label: 'settings.contacts.addressbooks.label',
                //     feature: EDisabledFeatures.CONTACTS
                //   }
                // },
                {
                  name: ROUTE_NAMES.CONTACTS_CONTACTS,
                  path: 'contacts',
                  component: MyContactsView,
                  meta: {
                    protected: true,
                    contacts: true,
                    label: 'settings.contacts.myContacts.label',
                    feature: EDisabledFeatures.CONTACTS
                  }
                },
                {
                  name: ROUTE_NAMES.CONTACTS_GROUPS,
                  path: 'groups',
                  component: GroupsView,
                  meta: {
                    protected: true,
                    contacts: true,
                    label: 'settings.contacts.groups.label',
                    feature: EDisabledFeatures.CONTACTS
                  }
                }
              ]
            },
            {
              path: 'folders',
              name: ROUTE_NAMES.SETTINGS_FOLDERS,
              component: FoldersView,
              meta: {
                label: 'settings.folders.label',
                protected: true,
                feature: EDisabledFeatures.FOLDERS,
                featureInfo: 'settings.upgrade.viewList.folders'
              }
            },
            {
              path: 'filters',
              name: ROUTE_NAMES.SETTINGS_FILTERS,
              component: FiltersView,
              meta: {
                label: 'settings.filters.label',
                protected: true
              }
            },
            // The flag is a build-time constant, so the route (and the lazy
            // import of the view) are eliminated from production builds where
            // the MCP server view is disabled.
            ...(isMcpServerEnabled
              ? [
                  {
                    path: 'mcp',
                    name: ROUTE_NAMES.SETTINGS_MCP_SERVER,
                    component: () => import('@/views/settings/McpServerView.vue'),
                    meta: {
                      label: 'settings.mcp.label',
                      protected: true
                    }
                  }
                ]
              : [])
          ]
        },
        {
          path: ':mailboxId',
          name: ROUTE_NAMES.MAILBOX,
          component: MailboxLayout,
          meta: {
            protected: true
          },
          children: [
            {
              path: ':threadId/:messageId',
              name: ROUTE_NAMES.MAILBOX_THREAD,
              component: MailboxLayout,
              meta: {
                protected: true
              }
            }
          ]
        },
        {
          path: 'search',
          name: ROUTE_NAMES.MAILBOX_SEARCH,
          component: MailboxLayout,
          meta: {
            protected: true
          },
          children: [
            {
              path: ':mailboxId/:threadId/:messageId',
              name: ROUTE_NAMES.MAILBOX_SEARCH_THREAD,
              component: MailboxLayout,
              meta: {
                protected: true
              }
            }
          ]
        }
      ]
    }
  ]
});

router.beforeEach(async (to) => {
  if (
    to.name &&
    [
      ROUTE_NAMES.AUTH_LAYOUT,
      ROUTE_NAMES.AUTH_LOGIN_PASSWORD,
      ROUTE_NAMES.AUTH_LOGIN_USER_SELECT,
      ROUTE_NAMES.AUTH_2FA,
      ROUTE_NAMES.AUTH_TOTP,
      ROUTE_NAMES.AUTH_WEBAUTHN
    ].includes(to.name.toString())
  ) {
    if (redirectToAuthApp(to)) {
      return false;
    }
  }
  // Redirect to inbox if no route is specified
  if (!to.name || to.path === '/') {
    const userIndex = await getFirstAvailableAccountIndex();
    if (userIndex !== undefined) {
      console.warn('Invalid path found, redirecting to first user base layout');
      return { name: ROUTE_NAMES.BASE_LAYOUT, params: { userIndex } };
    }
    return { name: ROUTE_NAMES.AUTH_LOGIN_PASSWORD };
  }

  if (to.meta.protected && to.params.userIndex) {
    const { get } = useCookies([]);
    const cookieDataForUserBeingAccessed = get<string | undefined>(
      `token_expiry_u_${to.params.userIndex}`
    );

    if (!cookieDataForUserBeingAccessed) {
      const { addSnackbar } = useSnackbar();
      const availableUserIndex = await getFirstAvailableAccountIndex();

      // TODO: Figure out a way to log out all of the expired users sessions
      axiosInstance.post(`/u/${to.params.userIndex}/auth/logout`).catch((e: Error) => {
        console.warn('Logout failed');
        Sentry.captureException(e, {
          data: `Logout failed for user ${to.params.userIndex}`
        });
      });
      if (availableUserIndex !== undefined) {
        // POST without await, because this can run in the background
        console.warn(
          'No token for current user found, but found token for next user. Redirecting to next user'
        );
        addSnackbar({
          type: 'info',
          message: await resolvedT('errors.session.redirectingToNextUser'),
          timeout: 10000
        });
        return { name: ROUTE_NAMES.BASE_LAYOUT, params: { userIndex: availableUserIndex } };
      }
      console.warn('No token found, redirecting to login');
      // POST without await, because this can run in the background
      axiosInstance.post(`/u/${to.params.userIndex}/auth/logoutAll`).catch((e: Error) => {
        console.warn('Logout failed');
        Sentry.captureException(e, {
          data: `Logout failed for user ${to.params.userIndex}`
        });
      });
      addSnackbar({
        type: 'attention',
        message: await resolvedT('errors.session.redirectingToLogin'),
        timeout: 10000
      });
      return {
        name: ROUTE_NAMES.AUTH_LOGIN_PASSWORD,
        query: {
          reason: 'missingExpiryCookie'
        }
      };
    }
  }
});

export default router;
